/* eslint-disable no-undef */
/* eslint-disable no-unused-expressions */
import React, { useMemo, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useIntl } from 'react-intl';

import Box from '@mui/material/Box';

import DescriptionIcon from '@mui/icons-material/Description';
import CopyIcon from '@mui/icons-material/FileCopy';
import LinkIcon from '@mui/icons-material/Link';

import Dialog, { useDialog } from '@ts-core/components/Dialog';
import SpeedDialMenu from '@ts-core/components/SpeedDialMenu';
import ExternalItemLinkForm from '@ts-core/core/components/ExternalItemLinkForm';

import { useDocuments, useDocumentUploader } from 'modules/documents/hooks';
import UpdateDocumentForm from 'modules/documents/components/UpdateDocumentForm';
import DocumentsTable from 'modules/documents/components/DocumentsTable';

import DocumentUploader from 'core/components/FileUploader';
import ImportSubjectItemsFromCourse from 'modules/subjects/components/ImportSubjectItemsFromCourse';

import { useImportDocumentsFromCourse } from '../../hooks';

import { DocumentFormInput } from '../../types';

const DocumentsPage = () => {
  const intl = useIntl();
  const documentSelectorRef = useRef<HTMLButtonElement>();
  const { courseId, subjectId } = useParams<{ courseId: string; subjectId: string }>();

  const { onCloseDialog: onCloseEditDocumentDialog, onOpenDialog: onOpenEditDocumentDialog, dialogProps: editDocumentDialogProps } = useDialog();

  const {
    onCloseDialog: onCloseImportDocumentsDialog,
    onOpenDialog: onOpenImportDocumentsDialog,
    dialogProps: importDocumentsDialogProps,
  } = useDialog();

  const {
    documents,
    loading,
    onDeleteDocument,
    onUpdateDocument,
    updatingDocument,
    onToggleDocument,
    onReorderDocuments,
    onImportDocuments,
    importingDocuments,
    onCreateExternalDocument,
    createExternalDocumentLoading,
    createExternalDocumentError,
  } = useDocuments({
    courseId,
    subjectId,
    onDocumentUpdated: onCloseEditDocumentDialog,
    onDocumentsImported: onCloseImportDocumentsDialog,
  });

  const handleImportDocuments = useCallback(
    (selectedDocuments: any) => {
      onImportDocuments({
        documents: selectedDocuments.map((d: any) => d._id),
      });
    },
    [onImportDocuments]
  );

  const importDocumentsFromCourseProps = useImportDocumentsFromCourse({
    courseId,
    importingDocuments,
    onConfirmSelection: handleImportDocuments,
  });

  const handleSelectDocument = useCallback((document: any) => {
    window.open(document.url, document.name);
  }, []);

  const handleAddDocument = useCallback(() => {
    documentSelectorRef?.current?.click();
  }, [documentSelectorRef]);

  const handleEditDocument = useCallback(
    (document: any) => {
      onOpenEditDocumentDialog({
        title: intl.formatMessage({ id: 'dialog.editDocument.title' }),
        dialogData: { document },
      });
    },
    [onOpenEditDocumentDialog, intl]
  );

  const handleOpenImportDocuments = useCallback(() => {
    onOpenImportDocumentsDialog({
      title: intl.formatMessage({ id: 'dialog.importDocuments.title' }),
    });
  }, [onOpenImportDocumentsDialog, intl]);

  /**
   * Create external document
   */
  const {
    onOpenDialog: onOpenCreateExternalDocument,
    onCloseDialog: onCloseCreateExternalDocument,
    dialogProps: createExternalDocumentDialogProps,
  } = useDialog();

  const handleOpenCreateExternalDocument = useCallback(() => {
    onOpenCreateExternalDocument({
      title: intl.formatMessage({ id: 'button.addDocumentLink.title' }),
    });
  }, [intl, onOpenCreateExternalDocument]);

  const handleCreateExternalDocument = useCallback(
    (input: DocumentFormInput) => {
      onCreateExternalDocument({ name: input.name, externalUrl: input.url }, onCloseCreateExternalDocument);
    },
    [onCloseCreateExternalDocument, onCreateExternalDocument]
  );

  const speedDialOptions = useMemo(
    () => [
      {
        icon: <LinkIcon />,
        title: intl.formatMessage({ id: 'button.addDocumentLink.title' }),
        onClick: handleOpenCreateExternalDocument,
      },
      {
        icon: <DescriptionIcon />,
        title: intl.formatMessage({ id: 'button.addDocument.title' }),
        onClick: handleAddDocument,
      },
      {
        icon: <CopyIcon />,
        title: intl.formatMessage({ id: 'dialog.importDocuments.title' }),
        onClick: handleOpenImportDocuments,
      },
    ],
    [handleAddDocument, handleOpenImportDocuments, intl, handleOpenCreateExternalDocument]
  );

  const documentUploaderProps = useDocumentUploader({ courseId, subjectId });

  return (
    <Box position="relative">
      <DocumentUploader ref={documentSelectorRef} {...documentUploaderProps} />
      <Dialog {...editDocumentDialogProps}>
        {(data) => (
          <UpdateDocumentForm
            isExternal={data?.document?.isExternal || false}
            documentFormData={data.document}
            onSubmit={onUpdateDocument}
            isSubmitting={updatingDocument}
          />
        )}
      </Dialog>
      <Dialog {...importDocumentsDialogProps}>{() => <ImportSubjectItemsFromCourse {...importDocumentsFromCourseProps} />}</Dialog>
      <Dialog {...createExternalDocumentDialogProps}>
        <ExternalItemLinkForm
          onSubmit={handleCreateExternalDocument}
          isSubmitting={createExternalDocumentLoading}
          submitError={createExternalDocumentError}
        />
      </Dialog>
      <DocumentsTable
        documents={documents}
        loading={loading}
        onRemove={onDeleteDocument}
        onEdit={handleEditDocument}
        onToggle={onToggleDocument}
        onReorder={onReorderDocuments}
        onSelect={handleSelectDocument}
      />
      <SpeedDialMenu options={speedDialOptions} />
    </Box>
  );
};

DocumentsPage.propTypes = {};

export default DocumentsPage;
